class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.emptyState = this.querySelector('[data-predictive-empty]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    // Monotonic id for the in-flight search. Only the latest request is allowed
    // to render, so superseded responses can never flash in over newer ones.
    this.latestRequestId = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('mousedown', this.onResultsMousedown.bind(this));
    this.addEventListener('click', this.onResultsClick.bind(this));

    if (this.emptyState) {
      this.emptyState.addEventListener('click', this.onEmptyStateClick.bind(this));
    }

    // Page size depends on the breakpoint (10 vs 6); re-slice the current page
    // when crossing it so the grid stays full and the chevron states stay right.
    this._breakpoint = window.matchMedia('(min-width: 990px)');
    this._onBreakpointChange = () => {
      if (this.productCards && this.productCards.length) this.renderProductPage(this.displayPage, true);
    };
    if (this._breakpoint.addEventListener) {
      this._breakpoint.addEventListener('change', this._onBreakpointChange);
    } else if (this._breakpoint.addListener) {
      this._breakpoint.addListener(this._onBreakpointChange); // Safari < 14
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // The new term doesn't extend the old one, so the current results are no
      // longer relevant. Drop them AND clear the `results` flag so the loading
      // skeleton shows (it's gated on [loading]:not([results])) instead of a
      // blank panel. When the term *does* extend the old one (refining), we keep
      // the results visible while loading for a smoother feel.
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.querySelector('.ps-noresults')?.remove();
      this.removeAttribute('results');
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Empty input supersedes any in-flight search: abort it and bump the token
      // so a late response (or its error handler) can't render or close the panel
      // out from under the recommendations.
      this.abortController.abort();
      this.abortController = new AbortController();
      this.latestRequestId++;
      // Empty input: show recommendations (when present) instead of tearing the panel down.
      if (this.emptyState) {
        this.showEmptyState();
      } else {
        this.close(true);
      }
      return;
    }

    this.hideEmptyState();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      // Invalidate any in-flight search so its response can't render after reset.
      this.latestRequestId++;
      if (this.emptyState) {
        // Cleared but still focused: fall back to the recommendations rather than closing.
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
        this.removeAttribute('results');
        this.showEmptyState();
      } else {
        this.closeResults(true);
      }
    }
  }

  onFocus() {
    // A programmatic focus from a chip click handles its own search — don't also
    // kick one off here, or two requests fire for the same term.
    if (this._skipNextFocusSearch) {
      this._skipNextFocusSearch = false;
      return;
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onEmptyStateClick(event) {
    const chip = event.target.closest('[data-predictive-term]');
    // Product cards in the empty state are real links — let them navigate normally.
    if (!chip) return;
    event.preventDefault();
    this.input.value = chip.getAttribute('data-predictive-term');
    // Refocus the input, but suppress the focus handler's own search so only the
    // explicit onChange() below runs (otherwise two requests fire → flash). The
    // finally guarantees the flag is cleared even if focus() throws, so a later
    // real focus is never wrongly skipped.
    this._skipNextFocusSearch = true;
    try {
      this.input.focus();
    } finally {
      this._skipNextFocusSearch = false;
    }
    this.onChange();
  }

  showEmptyState() {
    if (!this.emptyState) return;
    // Hide the results panel (it can still hold stale results or a "Search for X"
    // button from a previous query) and drop stale groups so only the recommendations show.
    this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    this.predictiveSearchResults.hidden = true;
    this.emptyState.hidden = false;
    this.open();
  }

  hideEmptyState() {
    if (!this.emptyState) return;
    this.emptyState.hidden = true;
    this.predictiveSearchResults.hidden = false;
  }

  onKeyup(event) {
    // Empty input: close, unless there's an empty state to keep open and arrow through.
    if (!this.getQuery().length && !this.emptyState) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    // Empty pager placeholders (.ps-card--empty) are skipped — they're spacers,
    // not options.
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null && !element.classList.contains('ps-card--empty')
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    // Keep the highlighted option visible — scrolls the products carousel
    // horizontally and the dropdown vertically as needed.
    activeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // A new search supersedes any in-flight one: cancel the previous request so
    // its (possibly different) response can never land and flash, and take a new
    // request token. This makes the dropdown render exactly once per search —
    // covering double-fired changes (chip click) AND out-of-order responses
    // while typing.
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.latestRequestId;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const signal = this.abortController.signal;

    // Suggestions (predictive endpoint) and products (full Search API, uncapped)
    // are fetched in PARALLEL and merged into one markup BEFORE rendering, so the
    // dropdown appears once — fully populated, with the true "Results (N)" count
    // — instead of flashing the predictive shell first and filling it a moment
    // later. The loading spinner shows until everything is ready.
    //
    // We fetch the FIRST TWO server pages of products up front (≈20 cards) so the
    // user can page from screen 1 → screen 2 instantly without waiting on a fetch.
    const suggestionsRequest = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product,query,collection,page,article&resources[limit]=10&resources[limit_scope]=each`,
      { signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(
        (text) =>
          new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search')
            .innerHTML
      );

    // Products are optional — a products failure must never tear down the dropdown.
    // We fetch IN-STOCK first (availability=1, pages 1+2) plus the first page of
    // SOLD-OUT (availability=0) so we know the sold-out total up front. The pager
    // shows every in-stock item first, then the sold-out ones at the very end.
    const inStock1 = this.fetchProductsPage(searchTerm, 1, 1).catch(() => null);
    const inStock2 = this.fetchProductsPage(searchTerm, 2, 1).catch(() => null);
    const soldOut1 = this.fetchProductsPage(searchTerm, 1, 0).catch(() => null);

    Promise.all([suggestionsRequest, inStock1, inStock2, soldOut1])
      .then(([suggestionsMarkup, in1, in2, out1]) => {
        // Only the latest request may proceed — a superseded one (e.g. a double
        // change from a chip click, or an out-of-order response while typing)
        // must never render OR write to the shared cache.
        if (requestId !== this.latestRequestId) return;
        if (this.getQuery() !== searchTerm) return;

        const productsState = this.buildProductsState(in1, in2, out1);
        const composed = this.composeResults(suggestionsMarkup, productsState, searchTerm);
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = composed;
        });
        this.renderSearchResults(composed);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return; // aborted/superseded
        if (requestId !== this.latestRequestId) return; // a newer search is in charge
        this.close();
        throw error;
      });
  }

  // Fold the initial fetches (in-stock pages 1+2, sold-out page 1) into one
  // products state. The visible buffer (`cards`) holds ONLY in-stock items at
  // first; the sold-out page-1 cards are stashed and not flushed into the buffer
  // until every in-stock item has loaded — that's what keeps sold-out items at
  // the very end rather than interleaved. Stored on the composed result so a
  // cached re-render can rehydrate the pager without re-fetching.
  buildProductsState(in1, in2, out1) {
    // Fallback: if the availability filter yielded nothing but a plain query
    // would have (e.g. a store/theme without availability facets), treat the
    // in-stock pages as a normal combined list so the dropdown still works.
    const inStockTotal = in1 ? parseInt(in1.total, 10) || 0 : 0;
    const soldOutTotal = out1 ? parseInt(out1.total, 10) || 0 : 0;
    const total = inStockTotal + soldOutTotal;
    if (total <= 0) return null;

    const cards = [];
    let inStockPages = 0;
    let inStockHasNext = false;

    if (in1 && in1.cards.length) {
      cards.push(...in1.cards);
      inStockPages = 1;
      inStockHasNext = in1.hasNext;
    }
    if (in2 && in2.cards.length) {
      cards.push(...in2.cards);
      inStockPages = 2;
      inStockHasNext = in2.hasNext;
    }

    return {
      total: String(total),
      cards,
      inStockTotal,
      soldOutTotal,
      inStockPages,
      inStockHasNext,
      // Sold-out page 1 stashed (not yet in the visible buffer).
      soldOutStash: out1 && out1.cards.length ? out1.cards : [],
      soldOutPages: out1 ? 1 : 0,
      soldOutHasNext: out1 ? out1.hasNext : false,
    };
  }

  // Merge the products state into the suggestions markup. The products list is
  // left EMPTY in the markup (the JS paints one display-page after render); we
  // only stamp the true count here. The state itself rides along in a JSON
  // <script> so a cached render can rehydrate the pager without re-fetching.
  composeResults(suggestionsMarkup, productsState, searchTerm) {
    const doc = new DOMParser().parseFromString(suggestionsMarkup, 'text/html');

    // No products at all → if there are also no suggestions (queries, collections,
    // pages, articles), the panel would be empty. Show an explicit "no results"
    // message instead of an empty/glitchy dropdown.
    if (!productsState) {
      const hasSuggestions = doc.querySelector('.predictive-search__list-item, #predictive-search-results-groups-wrapper');
      if (!hasSuggestions) return this.noResultsMarkup(searchTerm);
      return suggestionsMarkup;
    }

    const countEl = doc.querySelector('[data-predictive-results-count]');
    if (countEl) countEl.textContent = productsState.total;

    // The section stamps --no-products when the PREDICTIVE endpoint found no
    // products, and the desktop CSS hides the second result group under that
    // class. Our products come from the full Search API (which also matches
    // tags — e.g. "Midwall Split"), so once we know we have products the class
    // must go, or the column we're about to build renders display:none.
    doc
      .querySelector('.predictive-search__results-groups-wrapper--no-products')
      ?.classList.remove('predictive-search__results-groups-wrapper--no-products');

    let pager = doc.querySelector('[data-ps-pager]');

    // The predictive endpoint only renders the products column when ITS product
    // count > 0. Our products come from the full Search API, which can find
    // matches the predictive endpoint misses (e.g. "laptopstandpro"). When that
    // happens there's no pager in the markup — build the products column
    // ourselves and append it so the results still show.
    if (!pager) {
      let groups = doc.querySelector('#predictive-search-results-groups-wrapper');
      if (!groups) {
        // Section rendered no structure (predictive endpoint found nothing) —
        // create the listbox + groups wrapper so the products column is laid out
        // exactly like a normal results render.
        let listbox = doc.getElementById('predictive-search-results');
        if (!listbox) {
          doc.body.insertAdjacentHTML(
            'afterbegin',
            '<div id="predictive-search-results" role="listbox"></div>'
          );
          listbox = doc.getElementById('predictive-search-results');
        }
        listbox.insertAdjacentHTML(
          'beforeend',
          '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions"></div>'
        );
        groups = doc.querySelector('#predictive-search-results-groups-wrapper');
      }
      groups.insertAdjacentHTML('beforeend', this.buildPagerMarkup(productsState.total));
      pager = doc.querySelector('[data-ps-pager]');
    }

    if (pager) {
      const store = doc.createElement('script');
      store.type = 'application/json';
      store.setAttribute('data-ps-state', '');
      store.textContent = JSON.stringify(productsState);
      pager.appendChild(store);
    }
    return doc.body.innerHTML;
  }

  // Build the products column (heading + chevrons + "View all" + empty list)
  // identical to the one in sections/predictive-search.liquid. Used when the
  // predictive endpoint didn't render a pager but the full search has products.
  buildPagerMarkup(total) {
    const prevLabel = 'Previous page';
    const nextLabel = 'Next page';
    return (
      '<div class="predictive-search__result-group">' +
      '<div class="predictive-search__products" data-ps-pager>' +
      '<div class="predictive-search__products-head">' +
      '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">' +
      `Results (<span data-predictive-results-count>${total}</span>)</h2>` +
      '<div class="predictive-search__head-controls">' +
      '<div class="ps-pager__nav" data-ps-nav hidden>' +
      `<button type="button" class="ps-pager__btn ps-pager__btn--prev" data-ps-prev tabindex="-1" aria-label="${prevLabel}" disabled>` +
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      `<button type="button" class="ps-pager__btn ps-pager__btn--next" data-ps-next tabindex="-1" aria-label="${nextLabel}">` +
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '</div>' +
      '<a class="ps-view-all" href="#" data-ps-view-all tabindex="-1">View all</a>' +
      '</div></div>' +
      '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul>' +
      '<a class="ps-view-all-bottom" href="#" data-ps-view-all tabindex="-1">View all results</a>' +
      '</div></div>'
    );
  }

  // Markup shown when a query returns nothing — keeps the dropdown open with a
  // clear message (+ the typed term) rather than an empty/0-height panel.
  noResultsMarkup(searchTerm) {
    const term = (searchTerm || this.getQuery() || '').trim();
    const safe = term.replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const forTerm = safe ? ` for &ldquo;<span class="ps-noresults__term">${safe}</span>&rdquo;` : '';
    return (
      `<div class="ps-noresults" role="status">` +
      `<p class="ps-noresults__title">No results${forTerm}</p>` +
      `<p class="ps-noresults__hint">Check your spelling or try a more general term.</p>` +
      `</div>`
    );
  }

  // Fetch one server page (10 products) from the full Search API section,
  // returning the total, whether there's a next page, and the card markup split
  // into one HTML string per <li> so pages of arbitrary size can be reassembled.
  //
  // sort_by=relevance (Shopify's default) so the dropdown shows the most RELEVANT
  // matches for the typed query — not the most expensive (which surfaced big TVs
  // for almost everything). `availability=1` filters to purchasable products;
  // sold-out is unpublished/hidden so we only ever fetch in-stock.
  fetchProductsPage(searchTerm, page, availability) {
    let url = `${routes.search_url}?q=${encodeURIComponent(
      searchTerm
    )}&type=product&sort_by=relevance&page=${page}&section_id=predictive-search-products`;
    if (availability === 0 || availability === 1) url += `&filter.v.availability=${availability}`;
    return fetch(url, { signal: this.abortController.signal })
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (text == null) return null;
        const wrapper = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-ps-products]');
        if (!wrapper) return null;
        return {
          total: wrapper.getAttribute('data-total'),
          hasNext: wrapper.getAttribute('data-has-next') === 'true',
          cards: Array.from(wrapper.querySelectorAll(':scope > li')).map((li) => li.outerHTML),
        };
      });
  }

  // ─── Products pager ──────────────────────────────────────────────────────
  // One in-memory array of card markup, displayed one page at a time. The
  // display page size depends on the breakpoint (2×5 desktop, 2×3 mobile); the
  // fetch chunk is always the server's 10, so the two are decoupled and a resize
  // simply re-slices the same data.

  pageSize() {
    return window.matchMedia('(min-width: 990px)').matches ? 10 : 6;
  }

  // Read the products state JSON the section render left in the pager, hydrate
  // the instance fields, and paint the first page. Called once per render.
  initProductsPager() {
    this.pager = this.querySelector('[data-ps-pager]');
    this.productsList = this.querySelector('#predictive-search-results-products-list');
    this.pagerNav = this.querySelector('[data-ps-nav]');
    this.prevBtn = this.querySelector('[data-ps-prev]');
    this.nextBtn = this.querySelector('[data-ps-next]');
    this.viewAllLink = this.querySelector('[data-ps-view-all]');

    // Point every "View all" link (top pill + the mobile bottom link) at the full
    // search results page for the current query. No sort_by, so it opens in the
    // search page's default RELEVANCE order — the same relevant order the dropdown
    // now shows — instead of forcing most-expensive-first (which buried the
    // relevant results under sold-out premium TVs).
    const term = this.getQuery();
    const viewAllHref = `${routes.search_url}?q=${encodeURIComponent(
      term
    )}&type=product&options[prefix]=last`;
    this.querySelectorAll('[data-ps-view-all]').forEach((link) => {
      link.href = viewAllHref;
    });

    this.productCards = [];
    this.productsTotal = 0;
    this.displayPage = 1;
    this.fetchingNextServerPage = false;
    this._nextPagePromise = null;
    this._pagerFading = false;
    clearTimeout(this._pagerFadeTimer);

    // Two-phase paging state (in-stock first, then sold-out).
    this.inStockTotal = 0;
    this.inStockPages = 0;
    this.inStockHasNext = false;
    this.soldOutTotal = 0;
    this.soldOutPages = 0;
    this.soldOutHasNext = false;
    this.soldOutStash = []; // sold-out p1 cards, held back until in-stock is done

    if (!this.pager || !this.productsList) return;

    const store = this.pager.querySelector('[data-ps-state]');
    if (store) {
      try {
        const state = JSON.parse(store.textContent);
        this.productCards = state.cards || [];
        this.productsTotal = parseInt(state.total, 10) || this.productCards.length;
        this.inStockTotal = state.inStockTotal || 0;
        this.inStockPages = state.inStockPages || 0;
        this.inStockHasNext = !!state.inStockHasNext;
        this.soldOutTotal = state.soldOutTotal || 0;
        this.soldOutPages = state.soldOutPages || 0;
        this.soldOutHasNext = !!state.soldOutHasNext;
        this.soldOutStash = state.soldOutStash || [];
      } catch (e) {
        /* malformed state — leave the pager empty */
      }
    }

    if (this.productCards.length) this.renderProductPage(1, true);
  }

  // Total display pages we can currently show given what's buffered. If the
  // server still has more, the true total may be higher — serverHasMore()
  // governs whether the next chevron stays enabled past the buffered end.
  displayPageCount() {
    return Math.max(1, Math.ceil(this.productCards.length / this.pageSize()));
  }

  // Paint whatever `this.displayPage` currently points at. Always reads the page
  // fresh (never captures a stale slice) so it's safe to call repeatedly while a
  // fade is mid-flight — the final paint always reflects the latest target.
  paintProductPage() {
    const size = this.pageSize();
    this.displayPage = Math.min(Math.max(1, this.displayPage), this.displayPageCount());
    const start = (this.displayPage - 1) * size;
    const slice = this.productCards.slice(start, start + size);

    let html = slice.join('');
    // Fixed cell count so the grid keeps its 2×N height even on a short last
    // page — fill the remainder with empty placeholders.
    const missing = size - slice.length;
    if (missing > 0) {
      html += '<li class="predictive-search__list-item ps-card--empty" aria-hidden="true"></li>'.repeat(missing);
    }
    this.productsList.innerHTML = html;
    this.productsList.classList.remove('is-leaving');
    this.updatePagerControls();
    this.maybePrefetchNextServerPage();
  }

  renderProductPage(page, instant = false) {
    this.displayPage = Math.min(Math.max(1, page), this.displayPageCount());

    if (instant || this.prefersReducedMotion()) {
      this._pagerFading = false;
      clearTimeout(this._pagerFadeTimer);
      this.paintProductPage();
      return;
    }

    // A fade is already running — just let it repaint the latest page when it
    // lands (displayPage was already updated above). Avoids stacking listeners.
    if (this._pagerFading) return;

    // Quick crossfade: fade out, swap to the (current) target page, fade in.
    this._pagerFading = true;
    this.productsList.classList.add('is-leaving');
    const swap = () => {
      this.productsList.removeEventListener('transitionend', swap);
      clearTimeout(this._pagerFadeTimer);
      this._pagerFading = false;
      this.paintProductPage();
    };
    this.productsList.addEventListener('transitionend', swap, { once: true });
    // Fallback in case transitionend doesn't fire (e.g. display:none ancestors).
    this._pagerFadeTimer = setTimeout(swap, 200);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // True when there are still products to fetch — either more in-stock pages, or
  // (once in-stock is exhausted) stashed/remaining sold-out pages.
  serverHasMore() {
    return this.inStockHasNext || this.soldOutStash.length > 0 || this.soldOutHasNext;
  }

  updatePagerControls() {
    if (!this.pagerNav) return;
    const onLastBuffered = this.displayPage >= this.displayPageCount();
    const canGoNext = !onLastBuffered || this.serverHasMore();
    const canGoPrev = this.displayPage > 1;

    // Show the nav only when there's somewhere to go.
    this.pagerNav.hidden = !(canGoNext || canGoPrev);
    if (this.prevBtn) this.prevBtn.disabled = !canGoPrev;
    if (this.nextBtn) this.nextBtn.disabled = !canGoNext;
  }

  // Grow the buffer by one server page. IN-STOCK pages come first; only once
  // they're exhausted do we flush the stashed sold-out page 1 and then fetch
  // further sold-out pages — so sold-out items always land at the very end.
  // Returns a promise resolving true once the buffer has grown.
  fetchNextServerPage() {
    if (this._nextPagePromise) return this._nextPagePromise;

    const term = this.getQuery();

    // Phase 1: more in-stock pages remain.
    if (this.inStockHasNext) {
      const nextPage = this.inStockPages + 1;
      this.fetchingNextServerPage = true;
      this._nextPagePromise = this.fetchProductsPage(term, nextPage, 1)
        .then((data) => {
          this.fetchingNextServerPage = false;
          this._nextPagePromise = null;
          if (!data || this.getQuery() !== term) return false;
          if (data.cards.length) this.productCards.push(...data.cards);
          this.inStockPages = nextPage;
          this.inStockHasNext = data.hasNext;
          this.updatePagerControls();
          return true;
        })
        .catch((error) => {
          this.fetchingNextServerPage = false;
          this._nextPagePromise = null;
          if (error?.code === 20) return false;
          return false;
        });
      return this._nextPagePromise;
    }

    // Transition: in-stock done — flush the already-fetched sold-out page 1.
    if (this.soldOutStash.length) {
      this.productCards.push(...this.soldOutStash);
      this.soldOutStash = [];
      this.updatePagerControls();
      return Promise.resolve(true);
    }

    // Phase 2: more sold-out pages.
    if (this.soldOutHasNext) {
      const nextPage = this.soldOutPages + 1;
      this.fetchingNextServerPage = true;
      this._nextPagePromise = this.fetchProductsPage(term, nextPage, 0)
        .then((data) => {
          this.fetchingNextServerPage = false;
          this._nextPagePromise = null;
          if (!data || this.getQuery() !== term) return false;
          if (data.cards.length) this.productCards.push(...data.cards);
          this.soldOutPages = nextPage;
          this.soldOutHasNext = data.hasNext;
          this.updatePagerControls();
          return true;
        })
        .catch((error) => {
          this.fetchingNextServerPage = false;
          this._nextPagePromise = null;
          if (error?.code === 20) return false;
          return false;
        });
      return this._nextPagePromise;
    }

    return Promise.resolve(false);
  }

  // When the user is within one display-page of the buffered end and there's
  // more to fetch, grow the buffer in the background so the next chevron stays
  // instant.
  maybePrefetchNextServerPage() {
    if (this.fetchingNextServerPage || !this.serverHasMore()) return;
    const size = this.pageSize();
    const bufferedAfterCurrent = this.productCards.length - this.displayPage * size;
    if (bufferedAfterCurrent >= size) return; // still have a full page in reserve
    this.fetchNextServerPage();
  }

  goToPage(delta) {
    const target = this.displayPage + delta;
    if (target < 1) return;

    // Target is already buffered — page immediately.
    if (target <= this.displayPageCount()) {
      this.renderProductPage(target);
      return;
    }

    // Target is past what's buffered. If the server has more, fetch it (prefetch
    // usually means it's already on the way), then advance. Show the fade so the
    // wait reads as a transition, not a freeze.
    if (!this.serverHasMore() && !this._nextPagePromise) return;
    if (!this.prefersReducedMotion()) {
      this._pagerFading = true;
      this.productsList.classList.add('is-leaving');
    }
    this.fetchNextServerPage().then(() => {
      this._pagerFading = false;
      const clamped = Math.min(target, this.displayPageCount());
      this.renderProductPage(clamped, true);
    });
  }

  // Keep focus on the search input when clicking a chevron so the dropdown's
  // focusout handler doesn't close it. preventDefault on mousedown stops the
  // focus shift; the click still fires.
  onResultsMousedown(event) {
    if (event.target.closest('[data-ps-prev], [data-ps-next]')) {
      event.preventDefault();
    }
  }

  onResultsClick(event) {
    if (event.target.closest('[data-ps-prev]')) {
      event.preventDefault();
      this.goToPage(-1);
      return;
    }
    if (event.target.closest('[data-ps-next]')) {
      event.preventDefault();
      this.goToPage(1);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.hideEmptyState();
    this.setAttribute('results', true);

    // Hydrate the products pager from the JSON state the markup carries and
    // paint the first page (2×5 desktop / 2×3 mobile).
    this.initProductsPager();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    // The no-results markup has no count element — fall back to its status text.
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    const noResults = this.querySelector('.ps-noresults');
    this.setLiveRegionText(countEl ? countEl.textContent : noResults ? noResults.textContent : '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
